import { isBaseProjectAPIError, isProjectApiMutationError } from "@api/project-api/project-api-errors";
import { ApiResponseError, getMutationErrorProblemsMessages } from "@faro-lotv/service-wires";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";

/**
 * Returns whether a response from as async thunk contains a payload.
 */
export function isSuccessResponse(result) {
  return !("error" in result);
}

/**
 * @returns Whether the provided data is an object with a message property.
 * @param data The data to check.
 */
function isErrorObjectWithMessage(data) {
  return typeof data === "object" && data !== null && "message" in data && typeof data.message === "string";
}

/**
 * Converts errors of unknown shape to a readable markup.
 */
export function getErrorDisplayMarkup(error) {
  // Added a try-catch block in case an exception is thrown while attempting to read the error
  try {
    // If error comes from the Project API client
    if (error instanceof ApiResponseError) {
      if (isProjectApiMutationError(error)) {
        return getMutationErrorProblemsMessages(error).join("\n");
      } else if (isBaseProjectAPIError(error)) {
        return error.body.error;
      } else {
        return error.status ? `Server request failed with status ${error.status}.` : "Server request failed.";
      }
    }

    // If error comes from one of our Stellar http clients
    if (HttpUtils.isFetchError(error)) {
      // If error comes from the Core API client return the message or error_v2 property
      if (FaroCoreApiUtils.Client.isResponseError(error.errorBody)) {
        const errorBody = error.errorBody;
        return errorBody.message || errorBody.error_v2;
      }

      // If error body is an object with a message property
      if (isErrorObjectWithMessage(error.errorBody)) {
        return error.errorBody.message;
      }

      // Otherwise default to the FetchError message
      return error.message;
    }

    // If error is a generic error
    if (error instanceof Error) {
      return error.message;
    }

    // If error is an object with a message property. Example: SerializedError from Redux Toolkit
    if (isErrorObjectWithMessage(error)) {
      return error.message;
    }

    // If error is a string
    if (typeof error === "string") {
      return error;
    }
  } catch (_) {
    return "An unknown error occurred.";
  }

  // If error is of unknown shape return a default message
  return "An unknown error occurred.";
}

/**
 * @returns Whether the error originates from the backend, meaning its message
 * is dynamic and may embed user data (names, emails, project names, etc.).
 * Such messages must be excluded from LocalizeJS translation to avoid leaking
 * user data. Errors thrown by the Dashboard itself (generic Error or string)
 * are static, translatable strings and return false.
 * @param error The error to classify.
 */
export function isErrorFromBackend(error) {
  // Errors surfaced by our API clients carry backend response text.
  if (error instanceof ApiResponseError || HttpUtils.isFetchError(error)) {
    return true;
  }

  // Real Error instances and plain strings are thrown by the Dashboard itself.
  if (error instanceof Error || typeof error === "string") {
    return false;
  }

  // Anything else (e.g. a serialized RTK error wrapping a backend response) is
  // treated as potentially containing user data.
  return isErrorObjectWithMessage(error);
}